// MOD-12 — içerik analitiği (content-performance) BE client.
//
// BE endpoints (AnalyticsController, hepsi `dashboard.read` ile korunur):
//   GET /api/v1/admin/analytics/content-performance          — top içerik (view/click), sayfalı
//   GET /api/v1/admin/analytics/content-performance/export   — streaming CSV (blob)
//
// `from`/`to` DateOnly (yyyy-MM-dd) ve BE'de zorunlu; UI hatası sessiz 400 yerine
// yüksek sesle patlasın diye API katmanında guard'lıyoruz (dashboardApi.series deseni).
// Not: public `POST /analytics/event` web (Faz 5) tarafından tetiklenir — admin panel
// tüketmez, bu yüzden bu client'ta yer almaz.

import { http, unwrapList, type ActionResponse } from './http';
import type {
  ContentPerformanceItem,
  ContentPerformancePage,
  ContentPerformanceQuery,
} from '@features/analytics/types';

interface ContentPerformanceDtoWire {
  entityType?: string | null;
  entityId?: string | null;
  title?: string | null;
  slug?: string | null;
  views?: number | null;
  clicks?: number | null;
}

const BASE = '/api/v1/admin/analytics/content-performance';

function mapItem(d: ContentPerformanceDtoWire): ContentPerformanceItem {
  return {
    entityType: d.entityType ?? '',
    entityId: d.entityId ?? '',
    title: d.title ?? '',
    slug: d.slug ?? '',
    views: d.views ?? 0,
    clicks: d.clicks ?? 0,
  };
}

/** `from`/`to` zorunlu; boşsa UI hatasını erken (senkron) fırlat. */
function baseParams(q: ContentPerformanceQuery): Record<string, string | number> {
  if (!q.from || !q.to) {
    throw new Error('analyticsApi.contentPerformance: `from` and `to` are required.');
  }
  const params: Record<string, string | number> = { from: q.from, to: q.to };
  if (q.entityType) params.entityType = q.entityType;
  return params;
}

export const analyticsApi = {
  /** En çok görüntülenen/tıklanan içerikler; tarih penceresi + opsiyonel entityType. */
  async contentPerformance(query: ContentPerformanceQuery): Promise<ContentPerformancePage> {
    const params = baseParams(query);
    params.page = query.page ?? 1;
    params.pageSize = query.pageSize ?? 20;
    const res = await http.get<ActionResponse<ContentPerformanceDtoWire>>(BASE, { params });
    const { items, pagination } = unwrapList(res);
    return {
      items: items.map(mapItem),
      total: pagination?.totalCount ?? items.length,
      page: pagination?.page ?? query.page ?? 1,
      pageSize: pagination?.pageSize ?? query.pageSize ?? 20,
    };
  },

  /**
   * Aynı pencere/entityType filtresiyle streaming CSV blob'u indirir. Yetki
   * `dashboard.read` — kimlik doğrulamalı blob fetch (window.open değil).
   */
  async exportCsv(query: ContentPerformanceQuery): Promise<Blob> {
    const params = baseParams(query);
    const res = await http.get(`${BASE}/export`, { params, responseType: 'blob' });
    return res.data as Blob;
  },
};
